import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { apiClient, getApiErrorMessage } from "@/lib/api";
import { SECTION_MAP } from "@/lib/sections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ReportsTable } from "@/pages/admin/ReportsTable";
import { ReportFormDialog } from "@/pages/admin/ReportFormDialog";
import { DeleteReportDialog } from "@/pages/admin/DeleteReportDialog";

export default function AdminSection() {
  const { sectionKey } = useParams();
  const section = SECTION_MAP[sectionKey];
  const [searchParams, setSearchParams] = useSearchParams();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const formRef = useRef(null);

  const load = useCallback(() => {
    if (!section) return;
    setLoading(true);
    apiClient.get("/admin/reports", { params: { section: sectionKey, search: search.trim() || undefined, page_size: 200 } })
      .then(({ data }) => setReports(data.items || []))
      .catch((e) => toast.error(getApiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [sectionKey, search, section]);

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  useEffect(() => {
    if (section && searchParams.get("new") === "1") {
      formRef.current?.openCreate();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey, searchParams]);

  if (!section) return <Navigate to="/admin" replace />;

  const toggleStatus = async (r) => {
    const next = r.status === "published" ? "draft" : "published";
    try {
      await apiClient.put(`/admin/reports/${r.id}`, { status: next });
      toast.success(next === "published" ? `${r.company_name} published` : `${r.company_name} unpublished`);
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/admin/reports/${deleteTarget.id}`);
      toast.success("Report deleted");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
      setDeleteTarget(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-chivo text-2xl font-bold text-gray-900">{section.label}</h1>
          <p className="text-sm text-gray-500">{reports.length} report{reports.length === 1 ? "" : "s"} in this section</p>
        </div>
        <Button data-testid={ADMIN.newReportButton} onClick={() => formRef.current?.openCreate()} className="bg-gray-900 hover:bg-gray-800">
          <Plus className="h-4 w-4 mr-1.5" /> Publish New Report
        </Button>
      </div>

      <div className="relative max-w-xs mb-4">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
        <Input data-testid={ADMIN.reportSearchInput} className="pl-8" placeholder="Search company or sector..."
          value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <ReportsTable
        reports={reports}
        loading={loading}
        search={search}
        onToggleStatus={toggleStatus}
        onEdit={(r) => formRef.current?.openEdit(r)}
        onDeleteRequest={setDeleteTarget}
      />

      <ReportFormDialog ref={formRef} sectionKey={sectionKey} sectionLabel={section.label} onSaved={load} />

      <DeleteReportDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} />
    </div>
  );
}
